import { PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { AttachmentSection } from '@components/Attachments'
import Modal from '@components/Modal'
import { usePatchFlightGroup } from '@hooks'
import { addLinkRow, MAX_LINKS, removeLinkRow, setLinkRow } from '@lib/links'
import { GetFlightGroupResponse } from '@orbit/api-types'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router-dom'
import {
	AddLinkButton,
	Button,
	ButtonGroup,
	FormDescription,
	FormGroup,
	FormLabel,
	FormTextArea,
	LinkInput,
	LinkRow,
	LinksSection,
	RemoveLinkButton,
} from '../../Accommodations/AddAccommodationModal/AddAccommodationModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { groupDraftFromFlightGroup, toGroupUpdatePayload, type FlightGroupDraft } from '../flightForm'
import { TestIds } from '@/testIds'

interface EditFlightGroupModalProps {
	isOpen: boolean
	onClose: () => void
	onDelete: () => void
	group: GetFlightGroupResponse
}

const EditFlightGroupModal: React.FC<EditFlightGroupModalProps> = ({ isOpen, onClose, onDelete, group }) => {
	const { tripId } = useParams<{ tripId: string }>()
	const patchFlightGroup = usePatchFlightGroup()

	const [draft, setDraft] = useState<FlightGroupDraft>(() => groupDraftFromFlightGroup(group))

	useEffect(() => {
		if (isOpen && group) {
			setDraft(groupDraftFromFlightGroup(group))
		}
	}, [isOpen, group])

	const canSubmit = !patchFlightGroup.isPending

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!tripId || !canSubmit) return
		try {
			await patchFlightGroup.mutateAsync({
				tripId,
				flightGroupId: group.id,
				data: toGroupUpdatePayload(draft),
			})
			toast.success('Flight updated!')
			onClose()
		} catch {
			toast.error('Unable to update flight. Please try again.')
		}
	}

	const formId = 'edit-flight-group-form'

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Flight"
			size="large"
			backgroundColor={backgroundOrange}
			closeOnBackdropClick={false}
			onDelete={onDelete}
			footer={
				<ButtonGroup>
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button
						type="submit"
						form={formId}
						variant="primary"
						disabled={!canSubmit}
						data-testid={TestIds.flight.saveButton}
					>
						{patchFlightGroup.isPending ? 'Saving…' : 'Save Changes'}
					</Button>
				</ButtonGroup>
			}
		>
			<form id={formId} onSubmit={handleSubmit}>
				<FormGroup>
					<FormLabel>Links</FormLabel>
					<FormDescription>Add booking confirmations or relevant URLs (max {MAX_LINKS})</FormDescription>
					<LinksSection>
						{draft.links.map((link, index) => (
							<LinkRow key={index}>
								<LinkInput
									type="text"
									value={link}
									onChange={e => setDraft(prev => ({ ...prev, links: setLinkRow(prev.links, index, e.target.value) }))}
									placeholder="e.g. https://airline.com/booking/..."
								/>
								<RemoveLinkButton
									type="button"
									onClick={() => setDraft(prev => ({ ...prev, links: removeLinkRow(prev.links, index) }))}
								>
									✕
								</RemoveLinkButton>
							</LinkRow>
						))}
						<AddLinkButton
							type="button"
							onClick={() => setDraft(prev => ({ ...prev, links: addLinkRow(prev.links) }))}
							disabled={draft.links.length >= MAX_LINKS}
						>
							<PlusIcon sx={{ fontSize: iconSizes.sm }} />
							Add Link
						</AddLinkButton>
					</LinksSection>
				</FormGroup>

				<FormGroup>
					<FormLabel htmlFor="edit-flight-notes">Notes</FormLabel>
					<FormDescription>Confirmation number, seat assignments, baggage info, etc.</FormDescription>
					<FormTextArea
						id="edit-flight-notes"
						data-testid={TestIds.flight.notesField}
						value={draft.notes}
						onChange={e => setDraft(prev => ({ ...prev, notes: e.target.value }))}
						placeholder="e.g. Confirmation ABC123, seat 14A…"
					/>
				</FormGroup>

				{tripId && (
					<AttachmentSection
						tripId={tripId}
						resourceType="FLIGHT_GROUP"
						resourceId={group.id}
						fileIds={group.fileIds || []}
					/>
				)}
			</form>
		</Modal>
	)
}

export default EditFlightGroupModal
